import {Injectable} from "@angular/core";
import {tap} from 'rxjs/operators';
import {Observable} from "rxjs/index";
import {HttpClient, HttpParams, HttpResponse} from "@angular/common/http";
import {DxRequestOptions} from "../domain/http/dx-request-options";
import {DxConfigService} from "./dx-config.service";
import {DxGlobalWaiterService} from "./dx-global-waiter.service";
import {HttpError} from "../domain/error/dx-http-error";


/**
 *  Сервис-обертка над ангуляровским `http`-клиентом.
 *  Создан для:
 *  1. Гибкое перенаправление приложения на различные сервера (схемы) бэкенда.
 *  2. Пока что глобальная индикация загрузки, отключаемая опционально для каждого `http`-запроса. В принципе, сейчас это можно переделать через перехватчик.
 *  @publicApi
 */
@Injectable()
export class DxHttpDispatcher {
  /**
   * Ключ флага показывать\не показывать индикацию загрузки в опциях http-запроса.
   * @name NO_WAITER_MODE
   * @type {string}
   * @defaultValue - `'NO_WAITER_MODE'`
   */
  public static NO_WAITER_MODE: string = 'NO_WAITER_MODE';

  /**
   * Паттерн регулярного выражения для проверки начинается ли эндпоин с http/https.
   * Это дает возможность пропускать полные url запросов в перемешку с относительными эндпоинтами через сервис.
   * При наличии паттерна в url добавление базовой части не происходит.
   * @name PATTERN_HAS_PROTOCOL
   * @type {string}
   * @defaultValue - `'^(http://|https://)'`
   */
  private static PATTERN_HAS_PROTOCOL: string = '^(http://|https://)';

  /**
   * Текущий протокол сетевого взаимодействия: http\https
   * @name _protocol
   * @type {string}
   */
  private _protocol: string;

  /**
   * Текущий ip-адресс бэкенда.
   * @name _host
   * @type {string}
   */
  private _host: string;

  /**
   * Текущий порт, на котром развернут бэкенд.
   * @name _port
   * @type {string}
   */
  private _port: string;

  /**
   * Коренвой сермет множества url-адресов API.
   * @name _backendApiRoot
   * @type {string}
   */
  private _backendApiRoot: string;

  /**
   * Результат конкатенации протокола, хоста, порта и коренвого сермета множества url-адресов API.
   * @name _backendApiUrl
   * @type {string}
   */
  private _backendApiUrl: string;

  /**
   * Признак использования in-memory бэкэнда.
   * @name _useInMemoryBackend
   * @type {boolean}
   */
  private _useInMemoryBackend: boolean = false;


  /**
   * Создает инстанс сервиса.
   * @constructor
   * @param {HttpClient} _http - http-сервис ангуляра.
   * @param {DxConfigService} _configService - сервис, предоставляющий доступ к конфигурации ядра.
   * @param {DxGlobalWaiterService} _waiterService - сервис упровления глобальным индикатором загрузки.
   */
  constructor(private _http: HttpClient,
              private _configService: DxConfigService,
              private _waiterService: DxGlobalWaiterService) {
    this._protocol = this._configService.httpProtocol;
    this._host = this._configService.host;
    this._port = this._configService.port;
    this._backendApiRoot = this._configService.backendApiRoot;
    this._backendApiUrl = this._buildApiUrl();
    this._useInMemoryBackend = this._configService.useInMemoryBackend;
  }


  /**
   * Возвращает полный url на основе эндпоинта.
   * @param endpoint - url-сегмент конкретного узла REST API. Например: bpm, reporting, nsi.
   * @return {string} - полный url на основе эндпоинта.
   * @public
   */
  public getUrl(endpoint: string): string {
    let addBaseUrl = !new RegExp(DxHttpDispatcher.PATTERN_HAS_PROTOCOL, 'i').test(endpoint);
    return addBaseUrl ? (this._backendApiUrl + '/' + endpoint) : endpoint;
  }


  /**
   * Загружает json-моки напрямую с файловой системы. Например, из папки `assets`.
   * @param {string} path - относительный путь до json-файла.
   * @param {DxRequestOptions} options - опции http-запроса (опциональный параметр).
   * @return {Observable<HttpResponse<Object>>} - обсервэбл-объект на `HttpResponse<Object>`.
   */
  public getMockedData(path: string, options?: DxRequestOptions): Observable<HttpResponse<Object>> {
    return this._http.get(path, this._prepareOptions(options));
  }

  /**
   * Враппер для `GET`-запроса.
   * @param endpoint относительная или абсолютная ссылка.
   * @param options - опции `http`-запроса.
   * @return {Observable<HttpResponse<Object>>} - обсервэбл-объект на `HttpResponse<Object>`.
   */
  public get(endpoint: string, options?: DxRequestOptions): Observable<HttpResponse<Object>> {
    if(this._noWaiter(options)) {
      this._deleteNoWaiterModeParam(options);
      return this._http.get(this.getUrl(endpoint), this._prepareOptions(options));
    }
    this._showWaiter();
    return this._http.get(this.getUrl(endpoint), this._prepareOptions(options)).pipe(
      tap(
        data => { this._hideWaiter(); },
        error => { this._hideWaiter(); }
      )
    );
  }


  /**
   * Враппер для `POST`-запроса.
   * @param endpoint относительная или абсолютная ссылка.
   * @param body - тело запросса.
   * @param options - опции `http`-запроса.
   * @return {Observable<HttpResponse<Object>>} - обсервэбл-объект на `HttpResponse<Object>`.
   */
  public post(endpoint: string, body: any, options?: DxRequestOptions): Observable<HttpResponse<Object>> {
    if(this._noWaiter(options)) {
      this._deleteNoWaiterModeParam(options);
      return this._http.post(this.getUrl(endpoint), body, this._prepareOptions(options));
    }
    this._showWaiter();
    return this._http.post(this.getUrl(endpoint), body, this._prepareOptions(options)).pipe(
      tap(
        data => { this._hideWaiter(); },
        error => { this._hideWaiter(); }
      )
    );
  }

  /**
   * Враппер для `PATCH`-запроса.
   * @param endpoint относительная или абсолютная ссылка.
   * @param body - тело запросса.
   * @param options - опции `http`-запроса.
   * @return {Observable<HttpResponse<Object>>} - обсервэбл-объект на `HttpResponse<Object>`.
   */
  public patch(endpoint: string, body: any, options?: DxRequestOptions): Observable<HttpResponse<Object>> {
    if(this._noWaiter(options)) {
      this._deleteNoWaiterModeParam(options);
      return this._http.patch(this.getUrl(endpoint), body, this._prepareOptions(options));
    }
    this._showWaiter();
    return this._http.patch(this.getUrl(endpoint), body, this._prepareOptions(options)).pipe(
      tap(
        data => { this._hideWaiter(); },
        error => { this._hideWaiter(); }
      )
    );
  }


  /**
   * Враппер для `PUT`-запроса.
   * @param endpoint относительная или абсолютная ссылка.
   * @param body - тело запросса.
   * @param options - опции `http`-запроса.
   * @return {Observable<HttpResponse<Object>>} - обсервэбл-объект на `HttpResponse<Object>`.
   */
  public put(endpoint: string, body: any, options?: DxRequestOptions): Observable<HttpResponse<Object>> {
    if(this._noWaiter(options)) {
      this._deleteNoWaiterModeParam(options);
      return this._http.put(this.getUrl(endpoint), body, this._prepareOptions(options));
    }
    this._showWaiter();
    return this._http.put(this.getUrl(endpoint), body, this._prepareOptions(options)).pipe(
      tap(
        data => { this._hideWaiter(); },
        error => { this._hideWaiter(); }
      )
    );
  }


  /**
   * Враппер для `DELETE`-запроса.
   * @param endpoint - относительная или абсолютная ссылка.
   * @param options - опции `http`-запроса.
   * @return {Observable<HttpResponse<Object>>} - обсервэбл-объект на `HttpResponse<Object>`.
   */
  public delete(endpoint: string, options?: DxRequestOptions): Observable<HttpResponse<Object>> {
    if(this._noWaiter(options)) {
      this._deleteNoWaiterModeParam(options);
      return this._http.delete(this.getUrl(endpoint), this._prepareOptions(options));
    }
    this._showWaiter();
    return this._http.delete(this.getUrl(endpoint), this._prepareOptions(options)).pipe(
      tap(
        data => { this._hideWaiter(); },
        error => { this._hideWaiter(); }
      )
    );
  }


  /**
   * Трансформирует респонс с ошибкой, полученный в ответ на реквест к REST API в единый стандарт ошибки.
   * @param errorResponse - респонс с ошибкой
   * @return HttpError - стандартизированный объект ошибки
   */
  public static handleHttpError(errorResponse: HttpResponse<any>): HttpError {
    const error = new HttpError();
    error.status = errorResponse.status;
    error.statusText = errorResponse.statusText;
    // error.message = (errorResponse.body && errorResponse.body.error) ? errorResponse.body.error : '';
    if (errorResponse['error'] && errorResponse['error']['error_description']) {
      error.message = errorResponse['error']['error_description']
    } else if (errorResponse['error'] && errorResponse['error']['error'] && errorResponse['error']['message']) {
      error.message = errorResponse['error']['message'];
      error.statusText = errorResponse['error']['error'];
    } else error.message = (errorResponse.body && errorResponse.body.error) ? errorResponse.body.error : '';
    return error;
  }

  /**
   * Модифицирует опции http-запроса.
   * @param {DxRequestOptions} options - объект с опциями http-запроса.
   * @return {DxRequestOptions} - модифицированный объект с опциями http-запроса.
   */
  private _prepareOptions(options: DxRequestOptions): DxRequestOptions {
    // Опция {observe: 'response'} заставляет HttpClient возвращать полный response
    // со всеми заголовками, а не только содержимое body
    if (!options) options = {observe: 'response', withCredentials: true};
    return options;
  }

  /**
   * Формирует полный url для http-запроса.
   * @return {string} - конкатенация протокола, хоста, порта и коренвого сермета множества url-адресов API.
   */
  private _buildApiUrl(): string {
    let fullUrl: string = this._protocol + ":" + "//" + this._host + ":" + this._port;
    if(this._backendApiRoot && this._backendApiRoot !== '') {
      fullUrl += '/' + this._backendApiRoot;
    }
    return this._useInMemoryBackend ? this._backendApiRoot : fullUrl;
  }

  /** Показывает индикатор загрузки. */
  private _showWaiter(): void {
    this._waiterService.showWaiter();
  }

  /** Скрывает индикатор загрузки. */
  private _hideWaiter(): void {
    this._waiterService.hideWaiter();
  }

  /**
   * Определяет на основе состава опций http-запроса показывать \ не показывать индикацию загрузки при волнении запроса.
   * @param {DxRequestOptions} options - объект с опциями http-запроса.
   * @return {boolean} - флаг показывать \ не показывать индикацию загрузки при волнении запроса.
   */
  private _noWaiter(options: DxRequestOptions): boolean {
    if(!options || !options.params) return false;
    const params = <HttpParams> options.params;
    return params.has(DxHttpDispatcher.NO_WAITER_MODE);
  }

  /**
   * Удаляет параметр `NO_WAITER_MODE` из состава опций http-запроса.
   * @param {DxRequestOptions} options - объект с опциями http-запроса.
   */
  private _deleteNoWaiterModeParam(options: DxRequestOptions): void {
    const params = <HttpParams> options.params;
    params.delete(DxHttpDispatcher.NO_WAITER_MODE);
  }
}
